import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import ChatBubbleDisclosure from '../ChatBubbleDisclosure';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { AnswerMode, FarRegime } from './types';

/**
 * The three user-facing FAR Version labels. The internal `regime` value is
 * NEVER shown — the spec forbids the word "regime" anywhere in the UI
 * (legacy-far-readers spec §4.1). These exact labels are reused across the
 * selector, copy, and the homepage tour for consistency (§4.8).
 */
const FAR_VERSION_OPTIONS: ReadonlyArray<{ value: FarRegime; label: string }> = [
  { value: 'rfo', label: 'Revolutionary FAR Overhaul (RFO)' },
  { value: 'legacy', label: 'Legacy FAR' },
  { value: 'both', label: 'Both' },
];

/**
 * The ONE shared "selected" visual, factored out so the freeform field, the
 * suggestion chips, the "use original" chip, and the FAR Version options all
 * read identically when chosen: a darkened/tinted primary-tinted background +
 * a THICK (2px) primary border. The heavier border (vs. the thin 1px border
 * on unselected/outlined elements) is what conveys selection now that the
 * floating "Selected" cue is gone. This is DELIBERATELY distinct from the
 * solid filled-primary Confirm button — selection is a tint + thick outline,
 * the action is a fill — so the chosen option never gets confused with the
 * action button.
 */
const SELECTED_SURFACE_SX = {
  bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.12),
  border: '2px solid',
  borderColor: 'primary.main',
} as const;

interface Props {
  /**
   * Suggested rewrites from the `clarification_needed` envelope. MAY be
   * empty — in the clear-query case the card renders in selector-only mode
   * (just the FAR Version control + Confirm).
   */
  enhancements: string[];
  /** The user's original query (the default selected question). */
  originalQuery: string;
  /**
   * The agent's FAR-version pre-selection (internal). Pre-selects the
   * "FAR Version" control; the user's choice is authoritative. Defaults to
   * `'rfo'` when absent.
   */
  regime?: FarRegime;
  /**
   * Legacy server capability list. It is intentionally ignored: choosing an
   * execution path is no longer a client-side product decision. Retaining the
   * optional field lets a browser safely render persisted clarification
   * envelopes written before that policy changed.
   */
  availableAnswerModes?: readonly AnswerMode[];
  /**
   * Resolve the clarification ONCE, with both the chosen question and the
   * chosen FAR Version:
   *   - a suggested chip      → that chip's text
   *   - typed free-text       → the typed text
   *   - "use my original"     → `originalQuery`
   * The PRESENCE of the resulting `final_question` makes the backend skip
   * the gate and answer directly; `regime` selects which corpus to read.
   */
  onResolve: (
    finalQuestion: string,
    regime: FarRegime
  ) => void;
  /** Disable interaction while a resubmit is in flight. */
  disabled?: boolean;
  /** Restore immutable saved choices while retaining the live clarification presentation. */
  readOnlySelection?: {
    question: string;
    regime: FarRegime;
    refinement?: string;
    versionOptions?: ReadonlyArray<{ value: FarRegime; label: string }>;
  };
}

/**
 * Transient interactive card rendered in place of an answer bubble when a
 * turn POST returns `status: "clarification_needed"`. It is NOT a persisted
 * turn — nothing about it is saved server-side.
 *
 * Behavior (legacy-far-readers spec §4.1): unlike the original
 * submit-on-select card, this collects BOTH a question choice AND a FAR
 * Version before submitting. Enhancement chips, the free-text refine field,
 * and "use my original" form a single-select question group (clicking
 * highlights, never submits). A single Confirm button calls
 * `onResolve(finalQuestion, regime)` exactly once.
 *
 * Tradeoff (accepted per product decision): this adds one confirmation
 * round-trip to every relevant query vs. the old straight-through clear
 * path; the pre-selected defaults keep it to a single Confirm click.
 *
 * See issues/20260610-far-chat-clarification-step §4.1 / §5 and
 * issues/20260612-legacy-far-readers_spec.md §4.1.
 */
const ClarificationCard: React.FC<Props> = ({
  enhancements,
  originalQuery,
  regime,
  onResolve,
  disabled: disabledProp = false,
  readOnlySelection,
}) => {
  const disabled = disabledProp || Boolean(readOnlySelection);
  // Free-text lives in its OWN state, independent of which option is the
  // active choice. It is NEVER cleared by selecting another option or by
  // blurring the field — typed text always survives so the user can type,
  // click a chip, then click back and find their wording intact.
  const [liveFreeText, setFreeText] = useState('');
  const freeText = readOnlySelection ? readOnlySelection.refinement || '' : liveFreeText;
  // Cap at 3 defensively even though the backend already returns ≤3.
  const chips = (enhancements || []).filter((e) => !!e && e.trim()).slice(0, 3);
  const trimmedFree = freeText.trim();

  // Single-select question group. There is NO default selection — the user
  // must explicitly pick a question option (a chip, "use original", or a
  // non-empty freeform entry) before Confirm enables. A chip selection holds
  // the chip index; the free-text option is selected by focusing/typing.
  type QuestionChoice =
    | { kind: 'original' }
    | { kind: 'chip'; index: number }
    | { kind: 'free' };
  const [liveQuestionChoice, setQuestionChoice] = useState<QuestionChoice | null>(
    null
  );

  const savedChipIndex = readOnlySelection ? chips.indexOf(readOnlySelection.question) : -1;
  const questionChoice: QuestionChoice | null = readOnlySelection
    ? readOnlySelection.question === originalQuery ? { kind: 'original' }
      : savedChipIndex >= 0 ? { kind: 'chip', index: savedChipIndex }
        : freeText.trim() === readOnlySelection.question ? { kind: 'free' } : null
    : liveQuestionChoice;

  // FAR Version selection — pre-filled from the agent hint, user-authoritative.
  // This KEEPS its default (unlike the question group, which starts unselected).
  const [liveSelectedRegime, setSelectedRegime] = useState<FarRegime>(
    regime ?? 'rfo'
  );
  const selectedRegime = readOnlySelection?.regime ?? liveSelectedRegime;

  const isChipSelected = (idx: number) =>
    questionChoice?.kind === 'chip' && questionChoice.index === idx;
  const isOriginalSelected = questionChoice?.kind === 'original';
  const isFreeSelected = questionChoice?.kind === 'free';

  // A valid question selection exists when: a chip is chosen, the original is
  // chosen, OR free-text is the active choice AND it has non-empty trimmed
  // text. Confirm is gated on this.
  const hasValidSelection = (): boolean => {
    if (questionChoice === null) return false;
    if (questionChoice.kind === 'chip') {
      return !!chips[questionChoice.index];
    }
    if (questionChoice.kind === 'free') {
      return trimmedFree.length > 0;
    }
    // original
    return true;
  };

  // In selector-only mode (no question chips at all) there is no question to
  // pick, so Confirm is gated solely on the FAR Version (always set). When
  // chips exist, a valid question selection is required.
  const confirmEnabled = chips.length > 0 ? hasValidSelection() : true;

  // Resolve the selected question text from the active choice.
  const resolvedQuestion = (): string => {
    if (questionChoice?.kind === 'chip') {
      return chips[questionChoice.index] ?? originalQuery;
    }
    if (questionChoice?.kind === 'free') {
      return trimmedFree || originalQuery;
    }
    return originalQuery;
  };

  // Single guarded entry point — never resolve while disabled (jsdom's
  // fireEvent bypasses MUI's `pointer-events: none`, so the disabled prop
  // alone is not enough to block synthetic clicks in tests / a11y tools).
  // Also never resolve without a valid selection.
  const handleConfirm = () => {
    if (disabled || !confirmEnabled) return;
    onResolve(resolvedQuestion(), selectedRegime);
  };

  return (
    <Box
      data-testid="clarification-card"
      data-role="assistant"
      data-read-only={Boolean(readOnlySelection) || undefined}
      sx={{
        alignSelf: 'flex-start',
        maxWidth: '85%',
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        bgcolor: (theme) => alpha(theme.palette.info.light, 0.45),
        color: 'text.primary',
        fontSize: '0.9rem',
        boxShadow: 1,
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.info.main, 0.55),
        wordBreak: 'break-word',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        ...(readOnlySelection && {
          '& .MuiChip-root.Mui-disabled': { opacity: 1, color: 'text.primary' },
          '& .MuiToggleButton-root.Mui-disabled': { color: 'text.primary' },
          '& .MuiToggleButton-root.Mui-selected.Mui-disabled': { color: 'primary.main' },
        }),
      }}
    >
      <ChatBubbleDisclosure key={originalQuery} label="question clarification form" preview="" alwaysCollapsible defaultExpanded title={<Typography variant="body2" sx={{ fontWeight: 600 }}>
        {chips.length > 0
          ? 'Confirm your question and FAR version'
          : 'Confirm the FAR version for your question'}
      </Typography>}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        data-testid="clarification-instruction"
      >
        {chips.length > 0
          ? 'Click on a suggestion or refine your question, choose a FAR version, then Confirm.'
          : 'Choose a FAR version, then Confirm.'}
      </Typography>

      {chips.length > 0 && (
        <Box
          data-testid="clarification-chips"
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}
        >
          {chips.map((text, idx) => {
            const selected = isChipSelected(idx);
            return (
              // Wrapped in a Box for consistent layout with the other option
              // groups. Selection is conveyed by the thicker tinted border on
              // the chip itself (SELECTED_SURFACE_SX), not a floating cue.
              <Box
                key={idx}
                sx={{ position: 'relative', display: 'inline-flex', maxWidth: '100%' }}
              >
                <Chip
                  label={text}
                  data-testid="clarification-chip"
                  data-selected={selected ? 'true' : 'false'}
                  clickable
                  disabled={disabled}
                  // Unselected chips stay plain/outlined. The selected one reuses
                  // the shared tinted + primary-border treatment (NOT a solid
                  // filled primary), so it reads the same as the freeform's
                  // selected state and stays distinct from the Confirm button.
                  color="default"
                  variant="outlined"
                  aria-pressed={selected}
                  onClick={() => {
                    if (disabled) return;
                    // Selecting a chip makes it the active choice. It does NOT
                    // touch the typed free-text — that value persists so the user
                    // can switch back to it later without retyping.
                    setQuestionChoice({ kind: 'chip', index: idx });
                  }}
                  size="small"
                  sx={{
                    height: 'auto',
                    maxWidth: '100%',
                    ...(selected && {
                      '&.MuiChip-outlined': SELECTED_SURFACE_SX,
                    }),
                    '& .MuiChip-label': {
                      whiteSpace: 'normal',
                      display: 'block',
                      py: 0.5,
                    },
                  }}
                />
              </Box>
            );
          })}

          {/*
            "Use original question" lives in the SAME single-select group as
            the suggestion chips so the selected highlight is visually
            consistent. It shows a FIXED italic label ("Use original question")
            rather than the actual original query text, and the italics
            distinguish the user's own wording from the AI-suggested rewrites.
          */}
          <Box
            sx={{ position: 'relative', display: 'inline-flex', maxWidth: '100%' }}
          >
            <Chip
              label="Use original question"
              data-testid="clarification-use-original"
              data-selected={isOriginalSelected ? 'true' : 'false'}
              clickable
              disabled={disabled}
              // Same as the suggestion chips: outlined when unselected, shared
              // tinted + primary-border treatment when selected. The italics
              // are preserved in both states.
              color="default"
              variant="outlined"
              aria-pressed={isOriginalSelected}
              aria-label="Use original question"
              onClick={() => {
                if (disabled) return;
                // Choosing the original makes it the active choice. It does NOT
                // touch the typed free-text — that value persists.
                setQuestionChoice({ kind: 'original' });
              }}
              size="small"
              sx={{
                height: 'auto',
                maxWidth: '100%',
                fontStyle: 'italic',
                ...(isOriginalSelected && {
                  '&.MuiChip-outlined': SELECTED_SURFACE_SX,
                }),
                '& .MuiChip-label': {
                  whiteSpace: 'normal',
                  display: 'block',
                  py: 0.5,
                  fontStyle: 'italic',
                },
              }}
            />
          </Box>
        </Box>
      )}

      {chips.length > 0 && (
        <Box
          data-testid="clarification-free-text-wrapper"
          data-selected={isFreeSelected ? 'true' : 'false'}
          sx={{
            position: 'relative',
            borderRadius: 1,
            // When freeform is the active choice, surround it with the SAME
            // shared tinted + primary-border "selected" treatment used by the
            // chips and FAR Version options. `p` adds breathing room for the
            // floating cue and the inner field's own border.
            ...(isFreeSelected && {
              ...SELECTED_SURFACE_SX,
              p: 0.5,
            }),
          }}
        >
          <TextField
            data-testid="clarification-free-text"
            value={freeText}
          onChange={(e) => {
            // The typed value is its own state and is NEVER discarded by other
            // selections or blur. Typing makes free-text the active choice;
            // emptying it (while free-text is active) drops back to "no
            // selection" so Confirm re-gates rather than silently using the
            // original.
            setFreeText(e.target.value);
            if (e.target.value.trim()) {
              setQuestionChoice({ kind: 'free' });
            } else if (questionChoice?.kind === 'free') {
              setQuestionChoice(null);
            }
          }}
          onFocus={() => {
            // Focusing the field makes it the active choice, giving an obvious
            // selected affordance. (Switching AWAY happens by clicking another
            // option, not by blurring — blur never clears or deselects.)
            if (!disabled) setQuestionChoice({ kind: 'free' });
          }}
          placeholder="Refine your question…"
          helperText="What you type here is the question that will actually be submitted and answered, so be as specific and detailed as possible."
          FormHelperTextProps={
            {
              'data-testid': 'clarification-free-text-helper',
            } as React.HTMLAttributes<HTMLParagraphElement>
          }
          color="primary"
          // Force the focused/primary outline whenever free-text is the active
          // choice so the selected state is obvious even without keyboard focus.
          focused={isFreeSelected || undefined}
          size="small"
          fullWidth
          multiline
          maxRows={4}
          disabled={disabled && !readOnlySelection}
          inputProps={
            {
              'aria-label': 'Refine your question',
              readOnly: Boolean(readOnlySelection),
              'data-selected': isFreeSelected ? 'true' : 'false',
            } as React.InputHTMLAttributes<HTMLTextAreaElement>
          }
          sx={{
            '& .MuiOutlinedInput-root': {
              // The selected tint is provided by the wrapper Box
              // (SELECTED_SURFACE_SX); the field itself stays transparent so the
              // tint shows through without doubling. The primary border below
              // keeps the input outline reading as the active/selected choice.
              bgcolor: 'transparent',
              transition: 'border-color 120ms, background-color 120ms',
              '& fieldset': {
                borderColor: isFreeSelected ? 'primary.main' : undefined,
                borderWidth: isFreeSelected ? 2 : 1,
              },
              // Keep the strong primary border even on hover when selected.
              '&:hover fieldset': {
                borderColor: isFreeSelected ? 'primary.main' : undefined,
              },
            },
          }}
          />
        </Box>
      )}

      {/* FAR Version selector — never the word "regime" in the UI. */}
      <Box data-testid="clarification-far-version" sx={{ mt: 0.5 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
        >
          FAR Version
        </Typography>
        <ToggleButtonGroup
          value={selectedRegime}
          exclusive
          size="small"
          aria-label="FAR Version"
          onChange={(_e, val: FarRegime | null) => {
            if (disabled || val === null) return;
            setSelectedRegime(val);
          }}
          sx={{ flexWrap: 'wrap', gap: 0.5 }}
        >
          {(readOnlySelection?.versionOptions || FAR_VERSION_OPTIONS).map((opt) => {
            const selected = selectedRegime === opt.value;
            return (
              // Wrapped for consistent layout with the chips and freeform
              // field. Selection is conveyed by the thicker tinted border on
              // the toggle itself (SELECTED_SURFACE_SX), not a floating cue.
              <Box
                key={opt.value}
                sx={{ position: 'relative', display: 'inline-flex' }}
              >
                <ToggleButton
                  value={opt.value}
                  disabled={disabled}
                  data-testid={`far-version-${opt.value}`}
                  // Mirror the freeform/chips selected treatment: NOT a solid
                  // filled primary, but the shared tinted background + primary
                  // border so it reads identically and stays distinct from the
                  // solid Confirm button. `data-selected` is the stable test hook.
                  data-selected={selected ? 'true' : 'false'}
                  sx={{
                    textTransform: 'none',
                    py: 0.25,
                    px: 1,
                    borderColor: selected ? 'primary.main' : 'divider',
                    color: selected ? 'primary.main' : undefined,
                    ...(selected
                      ? {
                          ...SELECTED_SURFACE_SX,
                          '&.Mui-selected': {
                            ...SELECTED_SURFACE_SX,
                            color: 'primary.main',
                          },
                          '&.Mui-selected:hover': {
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.18),
                          },
                        }
                      : {}),
                  }}
                >
                  {opt.label}
                </ToggleButton>
              </Box>
            );
          })}
        </ToggleButtonGroup>
      </Box>

      <Button
        data-testid="clarification-confirm"
        aria-label={readOnlySelection ? 'Confirm (example action)' : undefined}
        variant="contained"
        size="small"
        // Disabled until a valid question option is selected (chip, "use
        // original", or non-empty freeform). In selector-only mode there is no
        // question to pick, so Confirm stays enabled.
        disabled={disabled || !confirmEnabled}
        onClick={handleConfirm}
        sx={{ alignSelf: 'flex-start', mt: 0.25 }}
      >
        Confirm
      </Button>
      </Box>
      </ChatBubbleDisclosure>
    </Box>
  );
};

export default ClarificationCard;
